// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// ** Components
import Link from '@/components/common/Link';
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';

// ** Utils
import { buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ** Types
import type { StrapiComponentElementProps } from '@/types/strapi';
import type { ComponentElementsImageGridCard as ComponentElementsImageGridCardProps } from '@/types/graphql';

type ComponentElementsImageGridCardType = StrapiComponentElementProps<
	Omit<ComponentElementsImageGridCardProps, 'id' | 'identifier' | '__typename'>
>;

const ComponentElementsImageGridCard = ({
	host,
	image,
	pageRelation,
	imageGridCaption
}: ComponentElementsImageGridCardType) => {
	const externalHref = pageRelation?.hrefExternal;
	const href = buildPathFromRelationLink(pageRelation?.href);
	const resolvedHref = href !== '/' ? href : externalHref || '/';

	return (
		<Box
			sx={{
				p: {
					xs: 0.5, // extra-small
					sm: 0.5, // small
					md: 0.5, // medium
					lg: 0.5, // large
					xl: 4 // extra-large
				},
				display: 'flex',
				flexDirection: 'column'
			}}
		>
			<Link href={resolvedHref} color="inherit">
				<Box
					sx={{
						position: 'relative',
						width: '100%',
						height: 300,
						overflow: 'hidden',
						borderRadius: 3,
						'&:hover .image-grid-card': {
							transform: 'scale(1.1)' // Zoom effect on hover
						}
					}}
				>
					{/* Image */}
					<StrapiImageRenderer
						host={host}
						imageData={image}
						fill
						className="image-grid-card"
						sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
						style={{
							transition: 'transform 0.3s ease-in-out' // Smooth zoom effect
						}}
					/>
				</Box>
			</Link>

			{/* Caption */}
			<Typography
				variant="caption"
				sx={{
					textAlign: 'center',
					mt: 2
				}}
			>
				{imageGridCaption}
			</Typography>
		</Box>
	);
};

export default ComponentElementsImageGridCard;
